import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Palette as PaletteIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../contexts/ApiContext';

/**
 * Panel de gestión de etiquetas personalizables por agente
 */
const TagManagementPanel = () => {
  const { user } = useAuth();
  const { agentTags } = useApi();
  const agentId = user?.username || user?.id;

  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Estados para el diálogo de crear/editar
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [tagName, setTagName] = useState('');
  const [tagColor, setTagColor] = useState('#2196F3');
  const [saving, setSaving] = useState(false);

  // Colores sugeridos
  const suggestedColors = [
    { name: 'Verde', value: '#4CAF50' },
    { name: 'Azul', value: '#2196F3' },
    { name: 'Naranja', value: '#FF9800' },
    { name: 'Gris', value: '#9E9E9E' },
    { name: 'Morado', value: '#9C27B0' },
    { name: 'Rojo', value: '#F44336' },
    { name: 'Cian', value: '#00BCD4' },
    { name: 'Rosa', value: '#E91E63' },
    { name: 'Lima', value: '#CDDC39' },
    { name: 'Ámbar', value: '#FFC107' }
  ];

  // Cargar etiquetas
  const loadTags = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      const result = await agentTags.getAll(agentId, { forceRefresh });

      if (!result.success) {
        throw new Error(result.message || 'Error al cargar etiquetas');
      }

      setTags(result.data || []);
    } catch (err) {
      console.error('[TAG MANAGEMENT] Error cargando etiquetas:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (agentId) {
      loadTags();
    }
  }, [agentId]);

  // Abrir diálogo para crear
  const handleCreate = () => {
    setEditingTag(null);
    setTagName('');
    setTagColor('#2196F3');
    setDialogOpen(true);
  };

  // Abrir diálogo para editar
  const handleEdit = (tag) => {
    setEditingTag(tag);
    setTagName(tag.tag_name);
    setTagColor(tag.tag_color);
    setDialogOpen(true);
  };

  // Guardar etiqueta (crear o editar)
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const token = localStorage.getItem('cti_admin_token');
      const isEditing = !!editingTag;

      const url = isEditing
        ? `/api/admin/agent-tags/${editingTag.id}`
        : '/api/admin/agent-tags';

      const method = isEditing ? 'PUT' : 'POST';

      const body = isEditing
        ? { tag_name: tagName.trim(), tag_color: tagColor }
        : { agent_id: agentId, tag_name: tagName.trim(), tag_color: tagColor };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(body)
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || `Error al ${isEditing ? 'actualizar' : 'crear'} etiqueta`);
      }

      setSuccess(isEditing ? 'Etiqueta actualizada' : 'Etiqueta creada');
      setDialogOpen(false);
      agentTags.invalidate(agentId);
      loadTags(true);

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('[TAG MANAGEMENT] Error guardando etiqueta:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Eliminar etiqueta
  const handleDelete = async (tagId, tagName) => {
    if (!window.confirm(`¿Eliminar la etiqueta "${tagName}"?`)) {
      return;
    }

    try {
      setError(null);

      const token = localStorage.getItem('cti_admin_token');
      const response = await fetch(`/api/admin/agent-tags/${tagId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Error al eliminar etiqueta');
      }

      setSuccess('Etiqueta eliminada');
      agentTags.invalidate(agentId);
      loadTags(true);

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('[TAG MANAGEMENT] Error eliminando etiqueta:', err);
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Mis Etiquetas
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Personaliza las etiquetas para organizar tus llamadas en seguimiento
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            size="small"
          >
            Nueva Etiqueta
          </Button>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Lista de etiquetas */}
        {tags.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No tienes etiquetas configuradas
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {tags.map((tag) => (
              <Grid item xs={12} sm={6} md={4} key={tag.id}>
                <Paper
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: tag.tag_color,
                    border: '2px solid',
                    borderColor: tag.tag_color,
                    '&:hover': {
                      boxShadow: 3,
                      transform: 'translateY(-2px)',
                      transition: 'all 0.2s'
                    }
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: 'white',
                      flex: 1
                    }}
                  >
                    {tag.tag_name}
                  </Typography>

                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(tag)}
                        sx={{ color: 'white' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(tag.id, tag.tag_name)}
                        sx={{ color: 'white' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Diálogo de crear/editar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTag ? 'Editar Etiqueta' : 'Nueva Etiqueta'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Nombre */}
            <TextField
              fullWidth
              label="Nombre de la etiqueta"
              value={tagName}
              onChange={(e) => setTagName(e.target.value)}
              placeholder="Ej: Cliente VIP"
              inputProps={{ maxLength: 50 }}
              helperText={`${tagName.length}/50 caracteres`}
            />

            {/* Color actual */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Color seleccionado
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: 2,
                    backgroundColor: tagColor,
                    border: '2px solid',
                    borderColor: 'divider'
                  }}
                />
                <TextField
                  type="color"
                  value={tagColor}
                  onChange={(e) => setTagColor(e.target.value.toUpperCase())}
                  sx={{ width: 100 }}
                />
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {tagColor}
                </Typography>
              </Box>
            </Box>

            {/* Colores sugeridos */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Colores sugeridos
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {suggestedColors.map((color) => (
                  <Tooltip key={color.value} title={color.name}>
                    <Box
                      onClick={() => setTagColor(color.value)}
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        backgroundColor: color.value,
                        border: '2px solid',
                        borderColor: tagColor === color.value ? 'primary.main' : 'divider',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          borderColor: 'primary.main'
                        }
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>

            {/* Preview */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Vista previa
              </Typography>
              <Chip
                label={tagName || 'Nombre de la etiqueta'}
                sx={{
                  backgroundColor: tagColor,
                  color: 'white',
                  fontWeight: 500
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!tagName.trim() || saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {editingTag ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TagManagementPanel;
